import React, { useCallback, useMemo } from 'react';
import { CalendarInputCommonProps } from './types';
import { DisplayMode } from '../types';
import { TimePicker, TimeChipInput } from '../../time-picker';
import { set, subMilliseconds } from 'date-fns';
import { HOUR_RESOLUTION, MINUTE_RESOLUTION } from './resolution';
import { applyItemInterval, getNewCursorItem, isSelectionFromAvailable, isSelectionToAvailable } from './utils';
import { DisabledTooltip } from '../../tooltip';
import { useProviderConfig } from '../../provider';

export interface TimeInputProps extends CalendarInputCommonProps {
  disabledWhenEmpty?: boolean;
  disabledTooltip?: string;
  displayMode?: DisplayMode;
  multiple?: boolean;
}

export const TimeInput: React.FC<TimeInputProps> = ({
  resolution = MINUTE_RESOLUTION,
  constraint,
  items,
  cursor,
  target,
  disabled,
  onCursorChange,
  disabledWhenEmpty,
  disabledTooltip,
  displayMode,
  multiple,
}) => {
  const { locale } = useProviderConfig();
  const currentTarget = target ?? cursor?.focus ?? 'from';
  const isFrom = currentTarget === 'from' || currentTarget === 'all';
  const currentDate = useMemo(
    () => (isFrom ? cursor?.item?.from : cursor?.item?.to && subMilliseconds(cursor.item.to, 1)),
    [cursor, isFrom],
  );
  const handleChange = useCallback(
    (date: Date | null) => {
      if (date == null) {
        return;
      }
      const searchInterval = resolution.resolve(date);
      const { item } = applyItemInterval(
        cursor?.item ?? getNewCursorItem(),
        constraint,
        { items },
        searchInterval,
        currentTarget,
      );
      onCursorChange({
        ...cursor,
        item,
        focus: currentTarget === 'all' ? 'from' : currentTarget,
        active: true,
      });
    },
    [constraint, currentTarget, cursor, items, onCursorChange, resolution],
  );
  const disabledTime = useCallback(() => {
    const date = currentDate ?? new Date();
    return {
      disabledHours: () => {
        const output = [];
        for (let hour = 0; hour < 24; hour += 1) {
          const interval = HOUR_RESOLUTION.resolve(set(date, { hours: hour }));
          if (isFrom) {
            if (!isSelectionFromAvailable(constraint, { items }, cursor?.item ?? {}, interval)) {
              output.push(hour);
            }
          } else {
            if (!isSelectionToAvailable(constraint, { items }, cursor?.item ?? {}, interval)) {
              output.push(hour);
            }
          }
        }
        return output;
      },
      disabledMinutes: (hour: number) => {
        const output = [];
        for (let minute = 0; minute < 60; minute += 1) {
          const interval = MINUTE_RESOLUTION.resolve(set(date, { hours: hour, minutes: minute }));
          if (isFrom) {
            if (!isSelectionFromAvailable(constraint, { items }, cursor?.item ?? {}, interval)) {
              output.push(minute);
            }
          } else {
            if (!isSelectionToAvailable(constraint, { items }, cursor?.item ?? {}, interval)) {
              output.push(minute);
            }
          }
        }
        return output;
      },
    };
  }, [currentDate, isFrom, constraint, items, cursor?.item]);

  const useChips = displayMode === 'bottom-sheet' && !multiple;
  const timeDisabled = disabled || (disabledWhenEmpty && currentDate == null);

  return (
    <DisabledTooltip
      disabled={!!disabledWhenEmpty && currentDate == null}
      content={disabledTooltip || locale?.TimePicker.disabledTooltipText}
      placement='top'
    >
      {useChips ? (
        <TimeChipInput
          value={currentDate ?? null}
          onChange={handleChange}
          disabledTime={disabledTime}
          hideDisabledMinutes
          disabled={timeDisabled}
        />
      ) : (
        <TimePicker
          usePortal={false}
          size='medium'
          value={currentDate}
          onChange={handleChange}
          disabledTime={disabledTime}
          hideDisabledMinutes
          disabled={timeDisabled}
        />
      )}
    </DisabledTooltip>
  );
};
